/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Every outbound link to a commercial partner, built one way: the partner
 * cards on /geen-laadpaal (src/pages/GeenLaadpaalPage.tsx) and the "Bekijk
 * bij Laadpaaldirect" links of the laadpalen catalog — the hub's model finder,
 * the brand pages and the model pages (src/lib/installerPartnerCatalog.ts;
 * until 2026-10-21 the register on /mid-check, src/components/MidRegister.tsx).
 *
 * Two things tell the partner a visitor came from us, and both are set here:
 *
 *  - UTM tags on the URL: `utm_source=ere-vergelijk`, `utm_medium=referral`,
 *    a campaign per surface and, where a surface has several links, a content
 *    tag per link. They survive what a header does not (an in-app browser, a
 *    copied link) and say WHICH link it was.
 *  - The Referer header. Helmet sends `Referrer-Policy: no-referrer` for the
 *    whole site (securityHeaders.ts), so by default a click leaves no trace at all. A
 *    `referrerpolicy` attribute on the link overrides the page's policy for
 *    that one navigation, and `strict-origin-when-cross-origin` sends our
 *    origin only (`https://ere-vergelijk.nl/`) — never the path or query of
 *    the page the visitor was on. `rel="noreferrer"` would cancel exactly
 *    that, so these links leave it out on purpose; `noopener` stays (the
 *    partner's page gets no handle on ours) and `sponsored` tells crawlers
 *    the link is a commercial relationship.
 *
 * Plain strings in, plain attributes out — no React, no window — so the bake
 * and the hydrating client render the same href.
 */

/** `utm_source` of every partner link. */
export const PARTNER_UTM_SOURCE = 'ere-vergelijk';

/** `utm_medium` of every partner link. */
export const PARTNER_UTM_MEDIUM = 'referral';

/** `rel` of every partner link — deliberately without `noreferrer` (see above). */
export const PARTNER_LINK_REL = 'noopener sponsored';

/** Our origin only, and only to an HTTPS page: the per-link answer to the site's `no-referrer`. */
export const PARTNER_REFERRER_POLICY = 'strict-origin-when-cross-origin';

export interface PartnerUrlTags {
  /** `utm_campaign`: the surface the link sits on (`geen-laadpaal`, `mid-register`). */
  campaign: string;
  /** `utm_content`: which link on that surface. Left off when absent or empty. */
  content?: string;
}

/**
 * `base` (an absolute URL) with the partner UTM tags set. Built on the URL
 * API, so a query string or fragment already on `base` is kept, and a tag it
 * already carries is replaced rather than doubled.
 */
export function partnerUrl(base: string, { campaign, content }: PartnerUrlTags): string {
  const url = new URL(base);
  url.searchParams.set('utm_source', PARTNER_UTM_SOURCE);
  url.searchParams.set('utm_medium', PARTNER_UTM_MEDIUM);
  url.searchParams.set('utm_campaign', campaign);
  if (content) url.searchParams.set('utm_content', content);
  // URLSearchParams writes a space as `+` (form encoding) and a literal `+`
  // as `%2B`, so every `+` left is a space. Spell it `%20`, which a receiver
  // decoding with decodeURIComponent also reads back as a space.
  url.search = url.searchParams.toString().replace(/\+/g, '%20');
  return url.toString();
}

/** The attributes of a partner link, spread onto the `<a>`. */
export interface PartnerLinkProps {
  href: string;
  target: '_blank';
  rel: typeof PARTNER_LINK_REL;
  referrerPolicy: typeof PARTNER_REFERRER_POLICY;
}

/** One set of attributes for every partner link: new tab, `noopener sponsored`, origin-only referrer. */
export function partnerLinkProps(href: string): PartnerLinkProps {
  return {
    href,
    target: '_blank',
    rel: PARTNER_LINK_REL,
    referrerPolicy: PARTNER_REFERRER_POLICY,
  };
}
